import React, { useMemo, useState } from 'react';
import {
  Drawer,
  Box,
  Stack,
  Typography,
  IconButton,
  Chip,
  Divider,
  Button,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Collapse,
  alpha,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

// Mirrors the Customer 365 Technical Health tab's CheckDetailDrawer pattern (right-anchored
// Drawer, collapsible Description, resolved Evaluation, per-target Results breakdown, raw JSON) -
// see src/pages/Customer/health/CheckDetailDrawer.js. Compliance Standards and Required Tier are
// intentionally omitted here - not applicable to UniFi operational checks.
// Generic across all three UniFi producers: each check's `describe(detail)` function (passed in
// via `meta`) knows how to render that specific checkKey's details[] shape as one line.

function resultMeta(result) {
  const r = ('' + result).toUpperCase();
  if (r === 'PASS') return { color: 'success', label: 'PASS' };
  if (r === 'WARN' || r === 'WARNING') return { color: 'warning', label: r === 'WARNING' ? 'WARNING' : 'WARN' };
  if (r === 'FAIL') return { color: 'error', label: 'FAIL' };
  return { color: 'default', label: result ? String(result) : '—' };
}

function StatusIcon({ result }) {
  const theme = useTheme();
  const map = {
    PASS: { icon: CheckCircleIcon, color: theme.palette.success.main },
    WARN: { icon: WarningAmberIcon, color: theme.palette.warning.main },
    WARNING: { icon: WarningAmberIcon, color: theme.palette.warning.main },
    FAIL: { icon: ErrorIcon, color: theme.palette.error.main },
    NODATA: { icon: HourglassEmptyIcon, color: theme.palette.text.disabled },
  };
  const entry = map[('' + result).toUpperCase()] || map.NODATA;
  const Icon = entry.icon;
  return <Icon sx={{ color: entry.color }} />;
}

function Row({ label, children }) {
  return (
    <Stack direction="row" spacing={1.5} sx={{ py: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110 }}>{label}</Typography>
      <Typography variant="caption" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>{children ?? '—'}</Typography>
    </Stack>
  );
}

function UnifiCheckDetailDrawer({ open, onClose, checkKey, check, meta, auditRunId }) {
  const theme = useTheme();
  const [showDesc, setShowDesc] = useState(false);
  const [showJson, setShowJson] = useState(false);
  const [copied, setCopied] = useState(false);

  const details = useMemo(() => check?.details || [], [check]);

  const tally = useMemo(() => {
    const t = { pass: 0, warn: 0, fail: 0 };
    for (const d of details) {
      const r = ('' + d.result).toUpperCase();
      if (r === 'PASS') t.pass += 1;
      else if (r === 'WARN' || r === 'WARNING') t.warn += 1;
      else if (r === 'FAIL') t.fail += 1;
    }
    return t;
  }, [details]);

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(check, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked - ignore */ }
  };

  if (!check) return null;

  const m = resultMeta(check.result);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, maxWidth: '100%' } }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <StatusIcon result={check.result} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{meta?.label || checkKey}</Typography>
          <Chip
            size="small"
            color={m.color}
            variant="outlined"
            label={m.label}
            sx={{ mt: 0.5, height: 20 }}
          />
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Stack>

      <Box sx={{ p: 2, overflowY: 'auto' }}>
        {/* Description (collapsed by default) */}
        {meta?.description && (
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" sx={{ cursor: 'pointer' }} onClick={() => setShowDesc(v => !v)}>
              {showDesc ? <KeyboardArrowDownRoundedIcon fontSize="small" /> : <KeyboardArrowRightRoundedIcon fontSize="small" />}
              <Typography variant="overline" color="text.secondary">Description</Typography>
            </Stack>
            <Collapse in={showDesc}>
              <Box sx={{ pl: 1, pt: 0.5 }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{meta.description}</Typography>
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Resolved evaluation */}
        <Typography variant="overline" color="text.secondary">Evaluation</Typography>
        <Box sx={{ mb: 2 }}>
          <Row label="Status">{('' + check.result).toLowerCase()}</Row>
          {check.message && <Row label="Message">{check.message}</Row>}
          <Row label="Source">{`audit · ${auditRunId || 'UNIFI'}${checkKey ? ` · ${checkKey}` : ''}`}</Row>
        </Box>

        {/* Per-target breakdown */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant="overline" color="text.secondary">
            Results ({details.length})
          </Typography>
          <Box sx={{ flex: 1 }} />
          {tally.pass > 0 && <Chip size="small" color="success" variant="outlined" label={`${tally.pass} pass`} sx={{ height: 18 }} />}
          {tally.warn > 0 && <Chip size="small" color="warning" variant="outlined" label={`${tally.warn} warn`} sx={{ height: 18 }} />}
          {tally.fail > 0 && <Chip size="small" color="error" variant="outlined" label={`${tally.fail} fail`} sx={{ height: 18 }} />}
        </Stack>
        {details.length === 0 ? (
          <Typography variant="caption" color="text.disabled">
            No exception rows - this check has no line items to itemize (all entities within policy).
          </Typography>
        ) : (
          <Table size="small" sx={{ mb: 2, '& td, & th': { px: 1, py: 0.5 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Target</TableCell>
                <TableCell>Result</TableCell>
                <TableCell>Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {details.map((detail, idx) => {
                const dm = resultMeta(detail.result);
                return (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ maxWidth: 150 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                        {detail.hostname || detail.target}
                      </Typography>
                      {detail.observedAt && (
                        <Typography variant="caption" color="text.disabled" display="block">
                          {String(detail.observedAt).slice(0, 10)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={dm.color} variant="outlined" label={dm.label} sx={{ height: 18 }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                        {meta?.describe ? meta.describe(detail) : JSON.stringify(detail)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {/* Raw JSON */}
        <Divider sx={{ my: 1 }} />
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button size="small" onClick={() => setShowJson(v => !v)}>{showJson ? 'Hide' : 'Show'} raw JSON</Button>
          <Box sx={{ flex: 1 }} />
          <Tooltip title={copied ? 'Copied' : 'Copy raw JSON'}>
            <span>
              <IconButton size="small" onClick={copyJson}><ContentCopyRoundedIcon fontSize="small" /></IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Collapse in={showJson}>
          <Box component="pre" sx={{
            mt: 1, p: 1.5, borderRadius: 1, bgcolor: alpha(theme.palette.text.primary, 0.04),
            fontSize: 11, lineHeight: 1.5, overflowX: 'auto', maxHeight: '40vh',
            border: `1px solid ${theme.palette.divider}`,
          }}>
            {JSON.stringify(check, null, 2)}
          </Box>
        </Collapse>
      </Box>
    </Drawer>
  );
}

export default UnifiCheckDetailDrawer;
